import React from 'react';
import {
    TableRow,
    TableCell,
    Checkbox,
    IconButton,
    Typography,
    Box,
    Avatar,
    Chip,
    Stack,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { formatDate } from '../../../utils/helpers';
import useCurrency from '../../../hooks/common/useCurrency';

const ProductsTableRow = ({
    item,
    index,
    page,
    perPage,
    isSelected,
    onRowCheckboxClick,
    onMenuOpen,
    isArabic,
    isDark
}) => {
    const lng = isArabic ? 'ar' : 'en';
    const { format: formatPrice } = useCurrency();

    const statusMap = {
        active: { color: 'success', label: { en: 'Active', ar: 'نشط' } },
        draft: { color: 'warning', label: { en: 'Draft', ar: 'مسودة' } },
        archived: { color: 'error', label: { en: 'Archived', ar: 'مؤرشف' } },
    };

    const status = statusMap[item.status] || { color: 'default', label: { en: item.status, ar: item.status } };

    // Calculate serial number
    const sn = (page - 1) * perPage + index + 1;

    return (
        <TableRow
            hover
            role="checkbox"
            aria-checked={isSelected}
            tabIndex={-1}
            selected={isSelected}
        >
            <TableCell padding="checkbox">
                <Checkbox
                    color="primary"
                    checked={isSelected}
                    onClick={(event) => onRowCheckboxClick(event, item.id)}
                />
            </TableCell>

            {/* S/N */}
            <TableCell sx={{ color: isDark ? 'grey.400' : 'grey.700' }}>
                {sn}
            </TableCell>

            {/* Product Image & Name */}
            <TableCell>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        src={item.cover?.thumb_url || item.cover?.url}
                        alt={item.name?.[lng]}
                        variant="rounded"
                        sx={{ width: 45, height: 45, bgcolor: 'grey.100' }}
                    >
                        {item.name?.[lng]?.charAt(0) || 'P'}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle2" noWrap sx={{ maxWidth: 200 }}>
                            {item.name?.[lng] || item.name?.en || '---'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {item.slug}
                        </Typography>
                    </Box>
                </Stack>
            </TableCell>

            {/* SKU */}
            <TableCell>
                <Typography variant="body2">{item.sku || '---'}</Typography>
            </TableCell>

            {/* Category */}
            <TableCell>
                {item.primary_category ? (
                    <Chip
                        label={item.primary_category.name?.[lng] || item.primary_category.name?.en}
                        size="small"
                        variant="outlined"
                    />
                ) : (
                    '---'
                )}
            </TableCell>

            {/* Brand */}
            <TableCell>
                {item.brand ? (
                    <Typography variant="body2">
                        {item.brand.name?.[lng] || item.brand.name?.en}
                    </Typography>
                ) : (
                    '---'
                )}
            </TableCell>

            {/* Price */}
            <TableCell>
                <Box>
                    <Typography variant="subtitle2" color="primary.main">
                        {formatPrice(item.price || 0)}
                    </Typography>
                    {item.compare_at_price && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textDecoration: 'line-through' }}
                        >
                            {formatPrice(item.compare_at_price)}
                        </Typography>
                    )}
                </Box>
            </TableCell>

            {/* Status */}
            <TableCell align="center">
                <Chip
                    label={status.label[lng]}
                    color={status.color}
                    size="small"
                    variant="soft"
                />
            </TableCell>

            {/* Created At */}
            <TableCell>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(item.created_at)}
                </Typography>
            </TableCell>

            <TableCell align="center">
                <IconButton onClick={(event) => onMenuOpen(event, item)}>
                    <MoreVertIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default ProductsTableRow;
